import ArgumentInputRow from '@/components/arguments/ArgumentInputRow';
import useAsyncArgumentValue from '@/hooks/useAsyncArgumentValue.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import selectors from '@/redux/selectors/selectors.js';
import argumentDataSources from '@/schema/argumentDataSources';
import appActions from '@/screens/apps/redux/actions/appActions.js';
import stepActions from '@/screens/workflows/redux/actions/stepActions.js';
import { activeWorkflowStep } from '@/screens/workflows/redux/selectors/workflowScreenSelectors';
import nestWorkflowArguments from '@/screens/workflows/workflowEditor/workflowEditorPanels/primaryPanels/appPanel/lib/nestWorkflowArguments.js';
import { getDeep } from '@shared/utils/getDeep.js';
import { isObject } from '@shared/utils/isObject.js';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

const isObjectWithKeys = (obj) => {
  return typeof obj === 'object' && obj !== null && Object.keys(obj).length > 0;
};

const NetSuiteCustomArguments = ({ path = 'options.customArguments', dataSource, dataSavePath, disabled = false }) => {
  const customDataSource = `${dataSource}-customArguments`;
  const stepDataSources = [argumentDataSources.workflowStep, argumentDataSources.workflowVariables, `${argumentDataSources.workflowStep}-customArguments`];
  const actions = stepDataSources.includes(dataSource) ? stepActions : appActions;

  const stepOutputs = useSelector(selectors.cookedWorkflowStepOutputs) || [];
  const options = useSelector(argumentSelectors[customDataSource].activeOptions);
  const args = useSelector(argumentSelectors[customDataSource].activeArguments());
  const step = dataSource === argumentDataSources.workflowStep ? useSelector(activeWorkflowStep) : {};
  const action = useSelector((state) => argumentSelectors[customDataSource].activeAction()(state));
  const actionArguments = useSelector((state) => argumentSelectors[customDataSource].activeAction()(state)?.arguments);
  const recordTypeArgDef = useSelector(
    memoizeSelector('recordTypeArgDef', (state) =>
      argumentSelectors[customDataSource]
        .activeAction()(state)
        ?.arguments?.find((n) => n.key === 'recordType')
    )
  );
  const recordTypeArg = useSelector(memoizeSelector('recordType', (state) => argumentSelectors[customDataSource].activeArguments()(state)?.recordType));
  const recordType = useAsyncArgumentValue({ arg: recordTypeArg, def: recordTypeArgDef, responses: stepOutputs });
  const isReqLoading = useArgSelector(selectors.isLoading, 'getNetsuiteRecordSchema');
  const hasLoaded = useSelector((state) => {
    if (!recordType) {
      return false;
    }
    const schema = state.workflow.netsuiteRecordSchema?.[recordType]?.schema?.fields;
    return schema?.length > 0 && isObject(schema?.[0]);
  });
  const customArguments = useMemo(() => {
    if (path === 'arguments' && isObjectWithKeys(step)) {
      return getDeep(path, step);
    }
    if (path === 'arguments') {
      return args;
    }
    return options?.customArguments || getDeep(path, step) || {};
  }, [path, step, options, args]);

  useEffect(() => {
    if (isReqLoading || !hasLoaded) {
      return;
    }

    const argsToRemove = Object.keys(customArguments)?.filter((key) => {
      const modifiedKey = key.replaceAll(/\[\d+\]/g, '[]');
      return !actionArguments?.find((arg) => arg.key === modifiedKey);
    });

    if (argsToRemove.length > 0) {
      actions.set(path, (prev) => {
        const newValues = { ...prev };
        argsToRemove.forEach((key) => {
          delete newValues[key];
        });
        return newValues;
      });
    }
  }, [customArguments, actionArguments, isReqLoading, hasLoaded]);

  const netsuiteArguments = useMemo(() => {
    return nestWorkflowArguments(actionArguments, customArguments, '');
  }, [actionArguments, customArguments]);

  return (
    <>
      {netsuiteArguments?.map((argument, idx) => (
        <ArgumentInputRow //
          className={'netsuite'}
          key={argument.id ? `${argument.id}-${idx}` : idx}
          argument={argument}
          definitions={actionArguments}
          definition={action}
          dataSource={`${dataSource}-customArguments`}
          dataSourcePath={path}
          dataSavePath={dataSavePath}
          disabled={disabled}
          simpleArguments={false}
        />
      ))}
    </>
  );
};

export default NetSuiteCustomArguments;
